// @ts-nocheck
import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarDays, Clock, FileText } from "lucide-react";
import SeoHead from "./SeoHead";
import { blogPosts, getBlogPost } from "../data/blogPosts";

const SITE_URL = "https://orionintelligence.net";

const colors = {
  bgTop: "#070b18",
  bgBottom: "#101827",
  primary: "#19E6FF",
  textMuted: "#a7b4c7",
  cardBg: "rgba(15, 23, 42, 0.62)",
  cardBorder: "rgba(255,255,255,0.12)",
};

function articleJsonLd(post) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.description,
    "datePublished": post.date,
    "dateModified": post.date,
    "author": {
      "@type": "Organization",
      "name": "Orion Intelligence",
      "url": SITE_URL,
    },
    "publisher": {
      "@type": "Organization",
      "name": "Orion Intelligence",
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/assets/orion-logo-800.png`,
      },
    },
    "mainEntityOfPage": `${SITE_URL}/blog/${post.slug}`,
  };
}

export function BlogIndexPage() {
  const sortedBlogPosts = [...blogPosts].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div
      className="min-h-screen text-white"
      style={{
        background: `radial-gradient(1000px 520px at 0% 0%, rgba(25,230,255,0.14), transparent 62%),
                     radial-gradient(900px 520px at 100% 0%, rgba(192,132,252,0.12), transparent 58%),
                     linear-gradient(${colors.bgTop}, ${colors.bgBottom})`,
      }}
    >
      <SeoHead
        title="Blog | Orion Intelligence"
        description="Άρθρα για custom software, AI αυτοματισμούς, invoice automation, legal tech και cloud εφαρμογές για επιχειρήσεις."
        canonical={`${SITE_URL}/blog`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Orion Intelligence Blog",
          "url": `${SITE_URL}/blog`,
          "description": "Άρθρα για custom software, AI automations και cloud εφαρμογές.",
        }}
      />

      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-300 hover:text-cyan-100">
          <ArrowLeft size={16} />
          Orion Intelligence
        </Link>

        <section className="pt-12">
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-cyan-200">
            <FileText size={14} />
            Blog
          </div>
          <h1 className="mt-5 text-3xl md:text-5xl font-black leading-tight">
            Άρθρα για software, AI και αυτοματισμούς
          </h1>
          <p className="mt-5 max-w-3xl text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
            Πρακτικές εξηγήσεις για επιχειρήσεις που θέλουν να καταλάβουν πότε αξίζει το custom software, πώς βοηθά το AI και πώς οργανώνονται καλύτερα οι διαδικασίες.
          </p>
        </section>

        <section className="grid md:grid-cols-3 gap-5 py-12">
          {sortedBlogPosts.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="rounded-2xl border p-6 block hover:bg-white/[0.06] transition-colors"
              style={{ background: colors.cardBg, borderColor: colors.cardBorder }}
            >
              <span className="rounded-full bg-cyan-400/10 px-3 py-1.5 text-xs font-bold text-cyan-200">
                {post.category}
              </span>
              <h2 className="mt-5 text-lg font-black leading-tight">{post.title}</h2>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: colors.textMuted }}>
                {post.description}
              </p>
              <div className="mt-5 flex flex-wrap gap-3 text-xs text-slate-400">
                <span className="inline-flex items-center gap-1"><CalendarDays size={13} /> {post.date}</span>
                <span className="inline-flex items-center gap-1"><Clock size={13} /> {post.readTime}</span>
              </div>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-cyan-300">
                Διαβάστε άρθρο
                <ArrowRight size={16} />
              </span>
            </Link>
          ))}
        </section>
      </main>
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const post = getBlogPost(slug) || blogPosts[0];
  const canonical = `${SITE_URL}/blog/${post.slug}`;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  return (
    <div
      className="min-h-screen text-white"
      style={{
        background: `radial-gradient(1000px 520px at 0% 0%, rgba(25,230,255,0.14), transparent 62%),
                     radial-gradient(900px 520px at 100% 0%, rgba(140,251,222,0.12), transparent 58%),
                     linear-gradient(${colors.bgTop}, ${colors.bgBottom})`,
      }}
    >
      <SeoHead
        title={`${post.title} | Orion Intelligence`}
        description={post.description}
        canonical={canonical}
        type="article"
        jsonLd={articleJsonLd(post)}
      />

      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-300 hover:text-cyan-100">
          <ArrowLeft size={16} />
          Blog
        </Link>

        <article className="pt-12">
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-cyan-400/10 px-3 py-1.5 text-xs font-bold text-cyan-200">
              {post.category}
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-slate-400"><CalendarDays size={13} /> {post.date}</span>
            <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Clock size={13} /> {post.readTime}</span>
          </div>
          <h1 className="mt-5 text-3xl md:text-5xl font-black leading-tight">
            {post.title}
          </h1>
          <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
            {post.description}
          </p>

          <div className="mt-10 space-y-8">
            {post.sections.map((section) => (
              <section key={section.heading} className="rounded-2xl border p-6" style={{ background: colors.cardBg, borderColor: colors.cardBorder }}>
                <h2 className="text-xl font-extrabold">{section.heading}</h2>
                <p className="mt-4 text-sm md:text-base leading-relaxed" style={{ color: colors.textMuted }}>
                  {section.body}
                </p>
              </section>
            ))}
          </div>

          <div className="mt-10 rounded-2xl border p-6" style={{ background: "rgba(255,255,255,0.04)", borderColor: colors.cardBorder }}>
            <h2 className="text-xl font-extrabold">Σχετικές υπηρεσίες</h2>
            <div className="mt-5 flex flex-wrap gap-3">
              {post.related.map((link) => (
                <Link key={link.href} to={link.href} className="rounded-full border border-white/10 px-4 py-2 text-sm font-bold text-cyan-200 hover:bg-white/5">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </article>
      </main>
    </div>
  );
}
